import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import type { VirtualElement } from '@popperjs/core';

export interface TooltipProps {
  message: React.JSX.Element;
  children: React.ReactNode;
  className?: string;
  // For the element wrapping the children (the popup itself takes className): sizing, where the wrapped
  // element must fill a grid cell or a flex row the way it did without the tooltip.
  wrapperClassName?: string;
  delay?: number;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  // Sticks to the pointer instead of the element (a wide element, where the tooltip would sit far from the
  // mouse); kept inside the window either way.
  followMouse?: boolean;
}
const Tooltip_: React.FC<TooltipProps> = ({ message, children, className, wrapperClassName, delay, placement = 'bottom', followMouse }) => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Where the pointer is, kept on the wrapper element (see virtualReference).
  const trackPointer = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.currentTarget.dataset.pointerX = String(event.clientX);
    event.currentTarget.dataset.pointerY = String(event.clientY);
  }, []);
  // Shows once the pointer is over the element: from enter, and from any move over it too, so an enter the
  // browser never delivered (a fast pointer, the element mounted or re-rendered under it) still shows it.
  const scheduleShow = useCallback(() => {
    if (timeoutRef.current) return;
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = undefined;
      setShowTooltip(true);
    }, delay || 0);
  }, [delay]);
  const hide = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = undefined;
    setShowTooltip(false);
  }, []);
  useEffect(() => () => clearTimeout(timeoutRef.current), []);
  const onMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      // The first frame at the pointer, not at 0,0.
      trackPointer(event);
      scheduleShow();
    },
    [scheduleShow, trackPointer],
  );
  const onMouseLeave = hide;
  // And the other way around: a leave the browser never delivered must not leave it open. While shown, any
  // pointer move outside the element hides it.
  useEffect(() => {
    if (!showTooltip || !referenceElement) return;
    const onPointerMove = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || !referenceElement.contains(event.target)) hide();
    };
    document.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => document.removeEventListener('pointermove', onPointerMove);
  }, [showTooltip, referenceElement, hide]);
  // Following the mouse: the popper is anchored to a point the pointer moves, not to the element. The point
  // lives on the wrapper element itself (data attributes the move handler writes), so the one virtual
  // reference stays the same object and popper is only updated on a move, never rebuilt.
  const virtualReference = useMemo<VirtualElement>(
    () => ({
      getBoundingClientRect: () => {
        const x = Number(referenceElement?.dataset.pointerX ?? 0);
        const y = Number(referenceElement?.dataset.pointerY ?? 0);
        return { width: 0, height: 0, top: y, right: x, bottom: y, left: x, x, y, toJSON: () => ({}) };
      },
    }),
    [referenceElement],
  );
  const { styles, attributes, update } = usePopper(followMouse ? virtualReference : referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      // Off the pointer, and never off the screen: pushed back inside the window, flipped to the other side when
      // there is no room.
      { name: 'offset', options: { offset: [0, followMouse ? 14 : 8] } },
      { name: 'preventOverflow', options: { padding: 8 } },
      { name: 'flip', options: { padding: 8 } },
    ],
  });
  const onMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      scheduleShow();
      if (!followMouse) return;
      trackPointer(event);
      update?.();
    },
    [followMouse, scheduleShow, trackPointer, update],
  );

  return (
    <>
      <div ref={setReferenceElement} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onMouseMove={onMouseMove} className={classNames('cursor-pointer', wrapperClassName)}>
        {children}
      </div>

      {showTooltip && (
        <div
          className={classNames(
            'text-sm bg-white dark:text-white dark:bg-[#313131] border rounded-md w-max border-neon-green z-50',
            // Never under the pointer's way: a fast pointer can land on the popup before it follows, which is outside
            // the element (a sibling, not a child) and would hide it.
            followMouse && 'pointer-events-none',
            !className?.includes('p-') && 'p-1',
            className,
          )}
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          {message}
          <div ref={setArrowElement} style={styles.arrow} />
        </div>
      )}
    </>
  );
};

export const Tooltip = memo(Tooltip_);
